import {Injectable, TemplateRef} from '@angular/core';
import {asapScheduler, BehaviorSubject, distinctUntilChanged, observeOn} from 'rxjs';

type ShellBreadcrumbBase = {
  id: string;
  label: string;
  route: string;
  icon?: string;
  relocationDropTarget?: {
    accepts: 'all' | 'container';
    destinationContainerId: string | null;
  };
};

export type ShellNavigationBreadcrumb = ShellBreadcrumbBase & {
  kind?: 'navigation';
};

export type ShellContainerBreadcrumb = ShellBreadcrumbBase & {
  kind: 'container';
  containerNavigation: {
    parentContainerId: string | null;
    parentPath: ShellContainerBreadcrumbPathSegment[];
  };
};

export type ShellBreadcrumb = ShellNavigationBreadcrumb | ShellContainerBreadcrumb;

export type ShellContainerBreadcrumbPathSegment = {
  id: string;
  name: string;
};

export function buildShellContainerBreadcrumbs(
  path: readonly ShellContainerBreadcrumbPathSegment[],
): ShellContainerBreadcrumb[] {
  return path.map((segment, index) => ({
    kind: 'container',
    id: segment.id,
    label: segment.name,
    route: `/inventory/containers/${segment.id}`,
    containerNavigation: {
      parentContainerId: path[index - 1]?.id ?? null,
      parentPath: path.slice(0, index).map((parentSegment) => ({
        id: parentSegment.id,
        name: parentSegment.name,
      })),
    },
  }));
}

export type ShellBreadcrumbTrailingActionTemplate = TemplateRef<unknown> | null;

function areBreadcrumbsEqual(left: ShellBreadcrumb[], right: ShellBreadcrumb[]): boolean {
  if (left.length !== right.length) {
    return false;
  }

  return left.every((item, index) => {
    const next = right[index];
    return item.id === next.id &&
      item.label === next.label &&
      item.route === next.route &&
      item.icon === next.icon &&
      (item.kind ?? 'navigation') === (next.kind ?? 'navigation') &&
      areContainerNavigationContextsEqual(item, next) &&
      item.relocationDropTarget?.accepts === next.relocationDropTarget?.accepts &&
      item.relocationDropTarget?.destinationContainerId === next.relocationDropTarget?.destinationContainerId;
  });
}

function areContainerNavigationContextsEqual(
  left: ShellBreadcrumb,
  right: ShellBreadcrumb,
): boolean {
  if (!isShellContainerBreadcrumb(left) || !isShellContainerBreadcrumb(right)) {
    return !isShellContainerBreadcrumb(left) && !isShellContainerBreadcrumb(right);
  }

  const leftContext = left.containerNavigation;
  const rightContext = right.containerNavigation;
  if (
    leftContext.parentContainerId !== rightContext.parentContainerId
    || leftContext.parentPath.length !== rightContext.parentPath.length
  ) {
    return false;
  }

  return leftContext.parentPath.every((segment, index) => {
    const next = rightContext.parentPath[index];
    return segment.id === next?.id && segment.name === next.name;
  });
}

export function isShellContainerBreadcrumb(breadcrumb: ShellBreadcrumb): breadcrumb is ShellContainerBreadcrumb {
  return breadcrumb.kind === 'container';
}

@Injectable({
  providedIn: 'root',
})
export class ShellBreadcrumbsService {
  private readonly breadcrumbsSubject = new BehaviorSubject<ShellBreadcrumb[]>([]);
  private readonly trailingActionTemplateSubject = new BehaviorSubject<ShellBreadcrumbTrailingActionTemplate>(null);

  readonly breadcrumbs$ = this.breadcrumbsSubject.asObservable().pipe(
    distinctUntilChanged(areBreadcrumbsEqual),
    observeOn(asapScheduler),
  );
  readonly trailingActionTemplate$ = this.trailingActionTemplateSubject.asObservable().pipe(
    distinctUntilChanged(),
    observeOn(asapScheduler),
  );

  setBreadcrumbs(breadcrumbs: ShellBreadcrumb[]): void {
    const current = this.breadcrumbsSubject.value;
    if (areBreadcrumbsEqual(current, breadcrumbs)) {
      return;
    }

    this.breadcrumbsSubject.next([...breadcrumbs]);
  }

  setTrailingActionTemplate(template: ShellBreadcrumbTrailingActionTemplate): void {
    if (this.trailingActionTemplateSubject.value === template) {
      return;
    }

    this.trailingActionTemplateSubject.next(template);
  }

  clearBreadcrumbs(): void {
    this.setBreadcrumbs([]);
  }

  clearTrailingActionTemplate(): void {
    this.setTrailingActionTemplate(null);
  }

  clear(): void {
    this.clearBreadcrumbs();
    this.clearTrailingActionTemplate();
  }
}
